import { DEVICE, TYPOGRAPHY } from '../../../common/assets/styles';
import { CrossIcon } from '../../../common/assets/svg';
import { FC, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { MethodologyContent } from './MethodologyContent';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const MethodologyModal: FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Reset scroll position when opening
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  if (!isOpen) return null;

  return createPortal(
    <Overlay onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-label={t('common.methodology.title')}>
      <Panel ref={panelRef}>
        <StickyHeader>
          <HeaderTitle>{t('common.methodology.title')}</HeaderTitle>
          <CloseButton type="button" onClick={onClose} aria-label={t('common.aria.close')}>
            <CrossIcon />
          </CloseButton>
        </StickyHeader>
        <ScrollContainer ref={scrollRef}>
          <MethodologyContent />
        </ScrollContainer>
      </Panel>
    </Overlay>,
    document.body,
  );
};

// ─── Animations ───

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(40px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

// ─── Styled Components ───

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  animation: ${fadeIn} 0.25s ease-out;
`;

const Panel = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--color5);
  overflow: hidden;

  animation: ${slideUp} 0.35s cubic-bezier(0.16, 1, 0.3, 1);

  @media ${DEVICE.laptop} {
    max-width: 820px;
    max-height: 90vh;
    height: auto;
    border-radius: 16px;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05);
  }
`;

const StickyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color10);
  background: var(--color5);
  flex-shrink: 0;
  z-index: 1;

  /* Glass effect */
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  @media ${DEVICE.laptop} {
    padding: 20px 32px;
    border-radius: 16px 16px 0 0;
  }
`;

const HeaderTitle = styled.h2`
  ${TYPOGRAPHY.bodySemibold18}
  color: var(--color6);
  margin: 0;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold24}
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }
  &:focus-visible {
    outline: 2px solid var(--color2);
    outline-offset: 2px;
  }
  & svg {
    width: 24px;
    height: 24px;
  }
  & path {
    stroke: var(--color6);
  }
`;

const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color7);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: var(--color9);
  }
`;
